import React, { useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import PrivateRoute from "./PrivateRoute/PrivateRoute";
import Loader from "../common/Loader/Loader";
import { RetryBanner } from "../common/comms/RetryBanner";
import { useOversightAccess } from "../hooks/comms/OversightAccessProvider";

/**
 * Oversight/analytics gate applied on top of an already-authorized route.
 *
 * Reads staff status from `OversightAccessProvider` (mounted above the
 * router in App.js) and renders the appropriate state:
 *   - loading      -> full-screen Loader
 *   - gatewayDown  -> full-page centered RetryBanner wired to retry()
 *   - not staff    -> redirect to /home
 *   - staff        -> render the wrapped element
 *
 * Calls `ensureProbe()` on mount so a login -> app transition (where the
 * provider's own mount-time probe was skipped for lack of a token yet)
 * still triggers the staff check once a token is present.
 *
 * @param {Object} props
 * @param {React.ComponentType} props.element - Component to render once authorized as staff.
 */
function OversightGate({ element: Component }) {
  const { isStaff, loading, gatewayDown, retry, ensureProbe } = useOversightAccess();

  useEffect(() => {
    ensureProbe();
  }, [ensureProbe]);

  if (loading) {
    return <Loader />;
  }

  if (gatewayDown) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full px-4">
        <RetryBanner
          message="Couldn't verify oversight access. Check your connection and try again."
          onRetry={retry}
        />
      </div>
    );
  }

  if (!isStaff) {
    return <Navigate to="/home" replace />;
  }

  return <Component />;
}

/**
 * Route guard usable as `element={<OversightRoute element={Component} />}`.
 *
 * Composes the existing `PrivateRoute` auth/access-control behavior (token
 * check, employee verification, route access) with the oversight staff gate
 * above, rather than duplicating PrivateRoute's logic: PrivateRoute decides
 * whether the user is authorized to be on the route at all, and only once it
 * renders its wrapped component do we apply the oversight gate on top.
 *
 * `Gated` is memoized on `Component` so its identity stays stable across
 * re-renders of the surrounding route tree (PrivateRoute renders it as
 * `<Component />`, so an unstable identity would force a remount every
 * render).
 *
 * @param {Object} props
 * @param {React.ComponentType} props.element - Component to render once authorized as staff.
 */
export function OversightRoute({ element: Component }) {
  const Gated = useMemo(
    () =>
      function OversightGated() {
        return <OversightGate element={Component} />;
      },
    [Component]
  );

  return <PrivateRoute element={Gated} />;
}

export default OversightRoute;
